/**
 * Shared state management for session replay event sender
 */

import type { RRWebEvent, TelemetryEvent, WindowWithReplay, NetworkEndpointStats } from './types';

// Get typed window reference
export const win = window as WindowWithReplay;

/**
 * Session state - holds all mutable state for a session
 */
export const sessionState = {
  // Configuration from script tag
  projectId: '' as string,
  backendUrl: 'https://xdabxhaxoqtqhfkunqjo.supabase.co/functions/v1/ingest-v2' as string,
  sendInterval: 3000 as number,

  // Session identifiers
  sessionId: '' as string,
  visitorId: '' as string,

  // Attribution (captured once at session start)
  referrer: '' as string,
  landingUrl: '' as string,

  // Device detection
  isMobile: false as boolean,

  // Event buffer
  events: [] as RRWebEvent[],
  currentBatchStartIndex: 0,

  // Structured telemetry events (for dashboard analytics)
  telemetryEvents: [] as TelemetryEvent[],

  // Recording state
  stopRecording: null as (() => void) | null,
  sessionEnded: false,
  sendIntervalId: null as number | null,
  idleCheckIntervalId: null as number | null,

  // Session metrics
  sessionStartTime: 0,
  errorCount: 0,
  consoleErrorCount: 0,
  warningCount: 0,
  networkErrorCount: 0,
  totalEventsSent: 0,

  // Activity tracking
  lastActivityTime: 0,
  isRecordingPaused: false,
  tabHiddenTime: null as number | null,

  // Event type counters for debugging
  eventTypeCounts: {} as Record<number, number>,

  // Per-endpoint network statistics (aggregated in memory, flushed per batch)
  networkEndpointStats: {} as Record<string, NetworkEndpointStats>,
};

/**
 * Initialize session state with configuration
 */
export function initializeState(config: {
  projectId: string;
  backendUrl: string;
  sendInterval: number;
  sessionId: string;
  isMobile: boolean;
  visitorId?: string;
  referrer?: string;
  landingUrl?: string;
}): void {
  sessionState.projectId = config.projectId;
  sessionState.backendUrl = config.backendUrl;
  sessionState.sendInterval = config.sendInterval;
  sessionState.sessionId = config.sessionId;
  sessionState.isMobile = config.isMobile;
  sessionState.visitorId = config.visitorId || '';
  sessionState.referrer = config.referrer || '';
  sessionState.landingUrl = config.landingUrl || '';

  // Initialize timing
  const now = Date.now();
  sessionState.sessionStartTime = now;
  sessionState.lastActivityTime = now;
  sessionState.networkEndpointStats = {};
}

/**
 * Reset state - useful for testing
 */
export function resetState(): void {
  sessionState.events = [];
  sessionState.currentBatchStartIndex = 0;
  sessionState.telemetryEvents = [];
  sessionState.stopRecording = null;
  sessionState.sessionEnded = false;
  sessionState.sendIntervalId = null;
  sessionState.idleCheckIntervalId = null;
  sessionState.sessionStartTime = 0;
  sessionState.errorCount = 0;
  sessionState.consoleErrorCount = 0;
  sessionState.warningCount = 0;
  sessionState.networkErrorCount = 0;
  sessionState.totalEventsSent = 0;
  sessionState.lastActivityTime = 0;
  sessionState.isRecordingPaused = false;
  sessionState.tabHiddenTime = null;

  sessionState.eventTypeCounts = {};
  sessionState.networkEndpointStats = {};
}
